import * as Sentry from '@sentry/react-native';
import { Stripe } from '@stripe/stripe-js';
import { useCallback, useEffect, useState } from 'react';

import { loadStripeWithRetry } from '@utils/payments/loadStripeWithRetry';

// Shared across mounts so reopening the subscription stack reuses the loaded
// instance; cleared on failure so the next attempt re-fetches Stripe.js.
let sharedStripePromise: Promise<Stripe> | null = null;

const getStripe = (publishableKey: string) => {
  if (!sharedStripePromise) {
    sharedStripePromise = loadStripeWithRetry(publishableKey).catch((error) => {
      sharedStripePromise = null;
      // Reported here, once per failed load rather than once per mounted
      // caller, so FRONTEND-2YT still shows how often users hit the
      // fallback. logNonFatalError is a no-op on web, hence Sentry directly.
      Sentry.captureException(error, { tags: { stripeLoad: 'failed' } });
      throw error;
    });
  }
  return sharedStripePromise;
};

export const resetStripeLoaderForTests = () => {
  sharedStripePromise = null;
};

export const useStripeLoader = (publishableKey: string) => {
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  // `loadFailed` stays true while a retry is in flight so the fallback (with
  // a spinner) stays up instead of flashing an empty checkout form
  const [retrying, setRetrying] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    getStripe(publishableKey).then(
      (instance) => {
        if (!active) return;
        setStripe(instance);
        setLoadFailed(false);
        setRetrying(false);
      },
      () => {
        if (!active) return;
        setLoadFailed(true);
        setRetrying(false);
      }
    );
    return () => {
      active = false;
    };
  }, [publishableKey, attempt]);

  // The Stripe instance loaded but the PaymentElement did not (e.g. offline
  // after the app-start preload); show the same fallback. Retry remounts the
  // checkout, which mounts a fresh element iframe.
  const reportLoadFailure = useCallback((error: Error) => {
    Sentry.captureException(error, { tags: { stripeLoad: 'element_failed' } });
    setLoadFailed(true);
    setRetrying(false);
  }, []);

  const retry = useCallback(() => {
    setRetrying(true);
    setAttempt((a) => a + 1);
  }, []);

  return { stripe, loadFailed, retrying, retry, reportLoadFailure };
};
